import * as signalR from '@microsoft/signalr';
import { BackgroundOperationStatus, SaBackgroundOperationStatus } from '../../api/spatial-adjust/models/sa-background-operation.model';
import { BehaviorSubject, firstValueFrom, timer } from 'rxjs';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AuthManagerService } from '../../api/auth/auth-manager.service';
import { BroadcastService } from './broadcast.service';
import { environment } from '../../../environments/environment';
import { GetCourseReportMessage } from '../../api/lynx-cloud/models/mqtt-get-course-report/get-course-report-message.model';
import { GetCoursesMessage } from '../../api/lynx-cloud/models/mqtt-get-courses/get-courses-message.model';
import { GetGeoTopoMessage } from '../../api/lynx-cloud/models/mqtt-get-geo-topo/get-geo-topo-message.model';
import { GetProgramTopoMessage } from '../../api/lynx-cloud/models/mqtt-get-program-topo/get-program-topo-message.model';
import { GetStationsMessage } from '../../api/lynx-cloud/models/mqtt-get-stations/get-stations-message.model';
import { LoggerService } from './logger.service';
import { LynxDriveService } from '../../api/lynx-drive/lynx-drive.service';
import { LynxPercentAdjust } from '../../api/lynx-cloud/models/mqtt-percent-adjust/lynx-percent-adjust.model';
import { PingResponse } from '../../api/lynx-cloud/models/mqtt-ping/ping-response.model';

type ConnState = 'connected' | 'reconnecting' | 'disconnected';

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
    private hub?: signalR.HubConnection;
    private shouldRun = true;
    private currentSiteId?: string;
    private joinedSiteId?: string;
    private startPromise?: Promise<void>;
    // private hasConnectedOnce = false;
    private networkAwarenessInitialized = false;
    private readonly onlineHandler = () => {
        if (!this.shouldRun || !this.hub || this.hub.state !== signalR.HubConnectionState.Disconnected) {
            return;
        }

        this.logger.info('[SignalR] browser online → retry start');
        void this.ensureStarted();
    };
    private readonly offlineHandler = () => {
        this.logger.warn('[SignalR] browser offline');
        this.zone.run(() => this.state$.next('disconnected'));
    };

    // expose state to components (banner/spinner, etc.)
    readonly state$ = new BehaviorSubject<ConnState>('disconnected');

    // =====================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =====================================================================================================================================================

    constructor(private authManager: AuthManagerService,
                private broadcastService: BroadcastService,
                private logger: LoggerService,
                private lynxDriveService: LynxDriveService,
                private zone: NgZone
    ) {}

    ngOnDestroy() {
        this.stop();
        this.state$.complete();
    }

    // =====================================================================================================================================================
    // Public Methods
    // =====================================================================================================================================================

    /** Call once when your app starts (or after login). Pass a siteId to auto-join a group. */
    start(siteId?: string) {
        this.shouldRun = true;
        this.currentSiteId = siteId;

        if (this.hub) {
            return this.ensureStarted();
        }

        this.hub = new signalR.HubConnectionBuilder()
            .withUrl(`${environment.dashApiUrl}/hubs/notifications`, {
                // return a fresh token each time (avoid stale JWTs during reconnect)
                accessTokenFactory: async () => {
                    return this.authManager.dashAuthenticatedUser.token;
                }
            })
            // more forgiving retry sequence than the default
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000, 30000])
            .configureLogging(signalR.LogLevel.Information)
            .build();

        // tune timeouts to cut down on spurious 1006s
        this.hub.serverTimeoutInMilliseconds = 30_000;     // server considered “silent” after 30s
        this.hub.keepAliveIntervalInMilliseconds = 15_000; // ping every 15s

        // =====================================================================================================================================================
        // API Server-to-client events - Lynx Drive
        // =====================================================================================================================================================

        this.hub.on('ping', payload => {
            this.zone.run(() => {
                this.logger.debug('Lynx ping response received', payload['correlation-data'])
                const response = new PingResponse(payload);
                this.lynxDriveService.pingResponseReceived.next(response);
            });
        });

        this.hub.on('get-courses', payload => {
            this.zone.run(() => {
                this.logger.info('Lynx get-courses response received')
                const courses = new GetCoursesMessage({ ...payload, installationId: payload['correlation-data'].installationId });
                this.lynxDriveService.courseListRetrieved.next(courses.content.result);
                this.clearLynxConnectionLoss();
            });
        });

        this.hub.on('get-program-topo', payload => {
            this.zone.run(() => {
                this.logger.info('Lynx get-program-topo response received')
                const topo = new GetProgramTopoMessage(payload);
                this.lynxDriveService.lynxPrograms = topo.content.result;
                this.lynxDriveService.programTopoRetrieved.next(topo.content.result);
                this.clearLynxConnectionLoss();
            });
        });

        this.hub.on('get-stations', payload => {
            this.zone.run(() => {
                this.logger.info('Lynx get-stations response received');
                const stations = new GetStationsMessage({ ...payload, installationId: payload['correlation-data'].installationId });
                this.lynxDriveService.lynxStations = stations.content.result;
                this.lynxDriveService.stationsRetrieved.next(stations.content.result);
                this.clearLynxConnectionLoss();
            });
        });

        this.hub.on('get-percent-adjustments', payload => {
            this.zone.run(() => {
                this.logger.info('Lynx get-percent-adjustments response received')

                this.lynxDriveService.lynxPercentAdjustments = payload.content.result.map(r => new LynxPercentAdjust(r));
                this.lynxDriveService.lynxPercentAdjustments.sort((a, b) => a.id - b.id);
                this.lynxDriveService.percentAdjustmentsRetrieved.next(this.lynxDriveService.lynxPercentAdjustments);
                this.clearLynxConnectionLoss();
            });
        });

        this.hub.on('get-geo-topo', payload => {
            this.zone.run(() => {
                this.logger.info('Lynx get-geo-topo response received')
                const topo = new GetGeoTopoMessage(payload);
                this.lynxDriveService.geoTopographyRetrieved.next(topo.content.result);
                this.clearLynxConnectionLoss();
            });
        });

        this.hub.on('get-course-report', payload => {
            this.zone.run(() => {
                this.logger.info('Lynx get-course-report response received')
                const courseReport = new GetCourseReportMessage(payload);
                this.lynxDriveService.courseReportRetrieved.next(courseReport.content.result);
                this.clearLynxConnectionLoss();
            });
        });

        // =====================================================================================================================================================
        // API Server-to-client events - General
        // =====================================================================================================================================================

        this.hub.on('turf-rad-retrieval-complete', payload => {
            this.zone.run(() => {
                this.logger.debug('TurfRad data retrieved.')
                this.broadcastService.saTurfRadScanDataRetrieved.next(payload);
            });
        });

        this.hub.on('target-profile-upsert-status', payload => {
            this.zone.run(() => {
                this.logger.debug('target-profile-upsert-status', payload);
                this.broadcastService.saTargetProfileUpsertStatus.next(new SaBackgroundOperationStatus(payload));

                if (payload?.status == BackgroundOperationStatus.Running) {
                    this.broadcastService.saFooterStatus.next(payload?.message);
                }
            });
        });


        // =====================================================================================================================================================
        // Hub lifecycle hooks
        // =====================================================================================================================================================

        this.hub.onreconnecting(err => {
            this.logger.warn('[SignalR] reconnecting…', err);
            this.zone.run(() => this.state$.next('reconnecting'));
        });

        this.hub.onreconnected(async _connId => {
            this.logger.info('[SignalR] reconnected');
            this.zone.run(() => this.state$.next('connected'));
            this.joinedSiteId = undefined;
            // groups are not sticky; re-join here
            if (this.currentSiteId) {
                try {
                    await this.rejoinCurrentSite();
                    this.logger.debug(`Re-joined site group ${this.currentSiteId}`);
                }
                catch (e) {
                    this.logger.error('JoinSite after reconnect failed', e);
                }
            }
        });

        this.hub.onclose(err => {
            this.joinedSiteId = undefined;
            this.logger.warn('[SignalR] connection closed', err);
            this.zone.run(() => this.state$.next('disconnected'));
        });

        // (optional) nudge reconnects on network changes
        this.initNetworkAwareness();

        return this.ensureStarted();
    }

    async stop() {
        this.shouldRun = false;
        this.startPromise = undefined;
        this.joinedSiteId = undefined;
        this.zone.run(() => this.state$.next('disconnected'));

        const hub = this.hub;
        this.hub = undefined;

        if (this.networkAwarenessInitialized) {
            window.removeEventListener('online', this.onlineHandler);
            window.removeEventListener('offline', this.offlineHandler);
            this.networkAwarenessInitialized = false;
        }

        await hub?.stop();
    }

    /** Call this if the user switches sites; will (re)join the new group. */
    async switchSite(siteId: string) {
        this.currentSiteId = siteId;
        if (this.hub?.state === signalR.HubConnectionState.Connected) {
            try {
                await this.rejoinCurrentSite();
                this.logger.debug(`Switched to site group ${siteId}`);
            }
            catch (e) {
                this.logger.error('JoinSite failed', e);
            }
        }
    }

    // =====================================================================================================================================================
    // Helper Methods
    // =====================================================================================================================================================

    /** Resilient initial start with exponential backoff. Post-connect recovery is handled by SignalR automatic reconnect. */
    private async startWithBackoff(): Promise<void> {
        let delayMs = 1000;
        const maxDelay = 30_000;

        while (this.shouldRun && this.hub) {
            try {
                if (this.hub.state === signalR.HubConnectionState.Connected) {
                    return;
                }

                await this.hub.start();
                this.zone.run(() => this.state$.next('connected'));
                this.logger.info('[SignalR] connected');
                // this.hasConnectedOnce = true;
                return;
            }
            catch (e) {
                this.logger.warn(`[SignalR] start failed; retrying in ${delayMs} ms`, e);
                await firstValueFrom(timer(delayMs));
                delayMs = Math.min(delayMs * 2, maxDelay);
            }
        }
    }

    /** Listen to browser online/offline to trigger reconnect attempts promptly. */
    private initNetworkAwareness() {
        if (this.networkAwarenessInitialized) {
            return;
        }

        window.addEventListener('online', this.onlineHandler);
        window.addEventListener('offline', this.offlineHandler);
        this.networkAwarenessInitialized = true;
    }

    private ensureStarted(): Promise<void> {
        if (this.hub == null) {
            return Promise.resolve();
        }

        if (this.startPromise) {
            return this.startPromise;
        }

        this.startPromise = this.startWithBackoff()
            .then(async () => {
                await this.rejoinCurrentSite();
            })
            .catch(err => {
                this.logger.error('SignalR start error', err);
                throw err;
            })
            .finally(() => {
                this.startPromise = undefined;
            });

        return this.startPromise;
    }

    private async rejoinCurrentSite(): Promise<void> {
        if (!this.hub || this.hub.state !== signalR.HubConnectionState.Connected || !this.currentSiteId) {
            return;
        }

        if (this.joinedSiteId && this.joinedSiteId !== this.currentSiteId) {
            await this.hub.invoke('LeaveSite', this.joinedSiteId);
        }

        if (this.joinedSiteId !== this.currentSiteId) {
            await this.hub.invoke('JoinSite', this.currentSiteId);
            this.logger.debug(`Joined site group ${this.currentSiteId}`);
        }

        this.joinedSiteId = this.currentSiteId;
    }

    clearLynxConnectionLoss() {
        this.broadcastService.saShowTopBanner.next(null);
    }

}
